import React, { useEffect, useMemo, useState } from 'react';
import { ThumbsDown, ThumbsUp, RefreshCw, ExternalLink, AlertTriangle } from 'lucide-react';
import AppShell from '../components/AppShell';
import axiosInstance from '../utils/axiosInstance';

const FILTERS = [
  { value: 'incorrect', label: 'Flagged incorrect', icon: ThumbsDown, tone: 'rose' },
  { value: 'correct', label: 'Approved', icon: ThumbsUp, tone: 'emerald' },
  { value: 'all', label: 'All feedback', icon: null, tone: 'stone' },
];

const Review = () => {
  const [filter, setFilter] = useState('incorrect');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get(`api/replai/review/?feedback=${filter}`);
      setData(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filter]);

  const updateFeedback = async (runId, feedback) => {
    try {
      await axiosInstance.patch(`api/replai/dispatch-runs/${runId}/feedback/`, { feedback });
      await load();
    } catch (e) {
      console.error(e);
    }
  };

  const header = (
    <div className="flex items-center gap-1 rounded-xl border border-border bg-white p-1">
      {FILTERS.map((f) => {
        const active = filter === f.value;
        const Icon = f.icon;
        return (
          <button
            key={f.value}
            type="button"
            onClick={() => setFilter(f.value)}
            className={`inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-medium transition ${
              active ? 'bg-[hsl(var(--accent))] text-foreground' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {Icon && <Icon className="h-3.5 w-3.5" />}
            {f.label}
          </button>
        );
      })}
    </div>
  );

  return (
    <AppShell
      title="Review queue"
      subtitle="Human feedback on Replai drafts. Incorrect flags feed future prompt and rule tweaks."
      headerRight={header}
    >
      {loading && !data && <p className="text-sm text-muted-foreground">Loading…</p>}
      {data && data.count === 0 && (
        <div className="rounded-2xl border border-border bg-white p-10 text-center text-sm text-muted-foreground">
          <AlertTriangle className="h-6 w-6 mx-auto mb-2 text-muted-foreground/60" />
          No drafts with this feedback yet.
        </div>
      )}
      {data && data.count > 0 && (
        <div className="space-y-3">
          {data.runs.map((r) => (
            <article
              key={r.run_id}
              className="bg-white rounded-2xl border border-border shadow-sm p-5"
            >
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="min-w-0">
                  <h3 className="font-display text-lg font-semibold text-foreground truncate">
                    {r.subject || '(no subject)'}
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    {r.customer_name || r.customer_email}
                    {' · '}
                    <span className="font-mono">{r.category}</span>
                    {' · '}
                    {new Date(r.human_feedback_at || r.created_at).toLocaleString()}
                  </p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <a
                    href={`https://lamptwist.gorgias.com/app/ticket/${r.gorgias_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs text-muted-foreground hover:bg-[hsl(var(--secondary))] hover:text-foreground"
                  >
                    <ExternalLink className="h-3 w-3" />
                    Gorgias
                  </a>
                  <button
                    type="button"
                    onClick={() =>
                      updateFeedback(r.run_id, r.human_feedback === 'correct' ? '' : 'correct')
                    }
                    className={`rounded-lg p-1.5 transition ${
                      r.human_feedback === 'correct'
                        ? 'bg-emerald-100 text-emerald-700'
                        : 'text-muted-foreground hover:bg-[hsl(var(--secondary))]'
                    }`}
                    title="Mark correct"
                  >
                    <ThumbsUp className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      updateFeedback(r.run_id, r.human_feedback === 'incorrect' ? '' : 'incorrect')
                    }
                    className={`rounded-lg p-1.5 transition ${
                      r.human_feedback === 'incorrect'
                        ? 'bg-rose-100 text-rose-700'
                        : 'text-muted-foreground hover:bg-[hsl(var(--secondary))]'
                    }`}
                    title="Flag incorrect"
                  >
                    <ThumbsDown className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>

              {r.human_feedback_note && (
                <div className="mb-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
                  <span className="font-medium">Flag note:</span> {r.human_feedback_note}
                </div>
              )}

              <div>
                <p className="text-[0.65rem] uppercase tracking-wider text-muted-foreground mb-1">Draft</p>
                <div className="rounded-xl bg-[hsl(var(--secondary))] p-4 text-sm whitespace-pre-wrap leading-relaxed">
                  {r.draft_reply || <span className="italic text-muted-foreground">(empty draft)</span>}
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </AppShell>
  );
};

export default Review;
